import React from 'react';
import { InfinityIcon } from 'lucide-react';
interface OverlayBox {
  left: string;
  top: string;
  width: string;
  height: string;
}

/**
 * Turns a call-to-action that is baked into the supplied artwork into a real
 * link that scrolls to the offer section. Percentage boxes track the artwork at
 * every screen size, so these stay tappable on phones too.
 */
export function ShopHotspot({
  box
}: {
  box: OverlayBox;
}) {
  return <a href="#offer" aria-label="Shop the GritVue X1. Go to pricing" className="absolute rounded-sm transition-shadow duration-150 ease-grit hover:ring-2 hover:ring-offwhite focus:outline-none focus-visible:ring-2 focus-visible:ring-offwhite" style={box} data-id="element-54" data-mp="3gf8sl1os974b" />;
}

/**
 * Turns a navigation label baked into the supplied artwork into a real in-page link.
 */
export function NavHotspot({
  box,
  href,
  label
}: {
  box: OverlayBox;
  href: string;
  label: string;
}) {
  return <a href={href} aria-label={label} className="absolute rounded-sm transition-shadow duration-150 ease-grit hover:ring-2 hover:ring-grit-500 focus:outline-none focus-visible:ring-2 focus-visible:ring-grit-500" style={box} data-id="element-55" data-mp="32giv3ky1x5ff" />;
}

/**
 * Covers an inactive secondary button baked into the supplied artwork ("See how
 * it works", "Watch it work", "See it in action"). On wide screens the space is
 * reused for the lifetime guarantee; on phones the artwork is too small for
 * legible type, so the button is simply blacked out and the guarantee is shown
 * full width below the image via GuaranteeStrip.
 */
export function GuaranteeBadge({
  box,
  compact = false
}: {
  box: OverlayBox;
  compact?: boolean;
}) {
  return <div className="absolute flex items-center justify-center gap-[0.4vw] bg-ink-950 lg:border lg:border-grit-500 lg:px-[0.5vw]" style={box} data-id="element-56" data-mp="3h9h6f32n7e4p">
      <InfinityIcon className={`hidden shrink-0 text-grit-500 lg:block ${compact ? 'h-[0.9vw] w-[0.9vw]' : 'h-[1.3vw] w-[1.3vw]'}`} aria-hidden="true" data-id="element-57" data-mp="2ti22j5v7worc" />
      <p className="hidden min-w-0 lg:block" data-id="element-58" data-mp="sgm4rph0ug3u">
        <span className={`block font-display font-extrabold uppercase leading-none text-grit-500 ${compact ? 'text-[0.62vw]' : 'text-[0.9vw]'}`} data-id="element-59" data-mp="sfugkzpr7knl">
          Lifetime guarantee
        </span>
        <span className={`mt-[0.1vw] block font-semibold leading-tight text-offwhite ${compact ? 'text-[0.4vw]' : 'text-[0.58vw]'}`} data-id="element-60" data-mp="169ljvqcw68av">
          It breaks, we replace it free.
        </span>
      </p>
    </div>;
}

/**
 * Blanks out a dead label baked into the artwork (the banner's old nav words) so
 * nothing on the image looks tappable when it isn't. Matches the header's black.
 */
export function ArtworkPatch({
  box
}: {
  box: OverlayBox;
}) {
  return <div aria-hidden="true" className="absolute bg-ink-950" style={box} data-id="element-61" data-mp="1tz62u991p5k5" />;
}

/**
 * The mobile counterpart to the guarantee badge: a full-width strip that reads
 * clearly on a phone instead of scaling with the artwork.
 */
export function GuaranteeStrip({
  className = ''
}: {
  className?: string;
}) {
  return <div className={`flex items-center justify-center gap-3 border-y-2 gold-edge bg-[#1a1408] px-4 py-3 text-center lg:hidden ${className}`} data-id="element-62" data-mp="242bfb3wzrgxd">
      <InfinityIcon className="h-6 w-6 shrink-0 text-[#f2c94c]" aria-hidden="true" data-id="element-63" data-mp="1z9i7262bdqgj" />
      <p className="text-left" data-id="element-64" data-mp="1va13ji6g73e0">
        <span className="block font-display text-lg font-extrabold uppercase leading-none gold-text" data-id="element-65" data-mp="1qawtyqfm33n5">
          Lifetime guarantee
        </span>
        <span className="mt-0.5 block text-[13px] font-medium text-steel-200" data-id="element-66" data-mp="1laiisziprdkw">
          It breaks, we replace it free. Forever.
        </span>
      </p>
    </div>;
}